import type { AccountInfo, EndSessionRequest, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

import {
  AcquireTokenAuthError,
  AcquiredTokenExpiredError,
  HandleTokenInUrlError,
  LoginInProgressAuthError,
  TokenExpirationParsingError,
} from '../errors';
import { getMsalErrorDetails } from '../getMsalErrorDetails';
import { getTokenExpirationTimeInSeconds, isTokenExpired } from '../jwt/decoder';
import type { Authenticator, AuthenticatorOptions } from '../types';
import { AuthenticatorType } from '../types';

import { loggerOptions } from './loggerOptions';
import { isIFrame } from './uri';

export const tenantsLocalStorageKey = 'yammer.login.tenants';
export const preventRedirectRequest = {
  onRedirectNavigate: (): boolean => false,
};

export default class MsalRedirectAuthenticator implements Authenticator {
  public readonly type = AuthenticatorType.MsalRedirect;
  private readonly application: PublicClientApplication;
  private currentAccount: AccountInfo | null;
  private readonly onError: AuthenticatorOptions['onError'];
  private readonly currentTenant: string;
  private tenants: Map<string, string>;
  private instance: string;

  constructor({ clientId, instance, onError, postLogoutRedirectUri, redirectUri, tenant }: AuthenticatorOptions) {
    this.tenants = new Map(this.getStoredTenants());
    this.currentTenant = tenant;
    this.instance = instance;
    this.application = new PublicClientApplication({
      auth: {
        authority: `${instance}${tenant}`,
        clientId,
        navigateToLoginRequestUrl: true,
        postLogoutRedirectUri,
        redirectUri,
        clientCapabilities: ['CP1'],
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions,
        allowRedirectInIframe: true,
      },
    });

    this.currentAccount = null;
    this.onError = onError;
  }

  private getStoredTenants() {
    const tenantsFromLocalStorage = window.localStorage.getItem(tenantsLocalStorageKey);

    return tenantsFromLocalStorage ? JSON.parse(tenantsFromLocalStorage) : '';
  }

  public async acquireToken(scopes: string[], claims?: string, tenantId?: string) {
    const currentAccount = this.getCurrentAccount();

    if (!currentAccount) {
      this.login(scopes);
      throw new LoginInProgressAuthError('Not yet authenticated. Redirecting to interactive login flow.');
    }

    const startTimestamp = Date.now();

    const account = tenantId
      ? {
          ...currentAccount,
          tenantId,
        }
      : currentAccount;

    const authority = tenantId ? { authority: `${this.instance}${tenantId}` } : {};
    const silentRequestClaims = claims ? { claims } : {};

    const silentRequest: SilentRequest = {
      account,
      scopes,
      ...authority,
      ...silentRequestClaims,
    };

    try {
      const { accessToken } = await this.application.acquireTokenSilent(silentRequest);

      this.reportAcquiredTokenExpiredError(accessToken, scopes, startTimestamp);

      return accessToken;
    } catch (error) {
      const errorDetails = getMsalErrorDetails(error);

      await this.reportError(new AcquireTokenAuthError({ ...errorDetails, attempts: 1 }), { scopes });

      if (error instanceof InteractionRequiredAuthError) {
        const redirectRequest: RedirectRequest = {
          ...silentRequest,
          redirectStartPage: window.location.href,
        };

        await this.application.acquireTokenRedirect(redirectRequest);
      }

      throw error;
    }
  }

  public acquireTokenRedirect() {
    throw new Error('acquireTokenRedirect is not implemented');
  }

  public getCurrentAccount() {
    return this.currentAccount;
  }

  private storeTenant(tenantId: string) {
    if (this.tenants.get(this.currentTenant)) {
      return;
    }

    this.tenants.set(this.currentTenant, tenantId);
    window.localStorage.setItem(tenantsLocalStorageKey, JSON.stringify(Array.from(this.tenants)));
  }

  public async initializeCurrentAccount() {
    await this.application.initialize();
    const cachedAccount = this.getCachedAccount();
    const redirectPromiseAccount = await this.getRedirectPromiseAccount();

    if (redirectPromiseAccount) {
      this.storeTenant(redirectPromiseAccount.tenantId);
      this.currentAccount = redirectPromiseAccount;
    } else if (cachedAccount) {
      this.currentAccount = cachedAccount;
    }
  }

  public logout() {
    window.localStorage.removeItem(tenantsLocalStorageKey);
    const endSessionRequest: EndSessionRequest | undefined = isIFrame() ? preventRedirectRequest : undefined;

    return this.application.logoutRedirect(endSessionRequest);
  }

  public setThirdPartyCookieErrorCallback() {
    throw new Error('setThirdPartyCookieErrorCallback is not implemented');
  }

  private getCachedAccount() {
    const accounts = this.application.getAllAccounts();
    if (accounts.length < 1) {
      return null;
    }

    return this.getAccountForTenant(accounts);
  }

  private getAccountForTenant(accounts: AccountInfo[]) {
    const tenantId = this.tenants.get(this.currentTenant);

    if (!tenantId) {
      return null;
    }

    return accounts.find((account) => account.tenantId === tenantId) ?? null;
  }

  private async getRedirectPromiseAccount() {
    try {
      const response = await this.application.handleRedirectPromise();

      return response?.account ?? null;
    } catch (error) {
      this.reportError(new HandleTokenInUrlError(error.message));

      return null;
    }
  }

  private login(scopes: string[]) {
    const loginRedirectRequest: RedirectRequest = {
      redirectStartPage: window.location.href,
      scopes,
    };

    this.application.loginRedirect(loginRedirectRequest);
  }

  private reportAcquiredTokenExpiredError(token: string, scopes: string[], startTimestamp: number) {
    let error: AcquiredTokenExpiredError | null = null;
    const endTimestamp = Date.now();

    try {
      if (isTokenExpired(token)) {
        const expiration = getTokenExpirationTimeInSeconds(token);

        error = new AcquiredTokenExpiredError({ expiration, startTimestamp, endTimestamp });
      }
    } catch (error) {
      this.reportError(new TokenExpirationParsingError(error), { scopes });
    }

    if (error) {
      this.reportError(error, { scopes });
    }
  }

  private async reportError(error: unknown, properties: { readonly scopes?: string[] } = {}) {
    // istanbul ignore else
    if (this.onError) {
      try {
        await this.onError(error, { authenticatorType: this.type, ...properties });
      } catch (err) {
        console.error(err);
      }
    }
  }
}
